"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { isOwnProfileRoute } from "@/helpers/ProfileHelpers";
import useCapacitor from "@/hooks/useCapacitor";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserPageTab from "./UserPageTab";
import { DEFAULT_USER_PAGE_TAB, USER_PAGE_TABS, USER_PAGE_TAB_IDS, type UserPageTabKey, type UserPageVisibilityContext, getUserPageTabByRoute } from "./userTabs.config";
const DEFAULT_TAB = DEFAULT_USER_PAGE_TAB;

// Normalize consent country to uppercase code; empty or non-strings become null.
const normalizeCountry = (country: string | null | undefined): string | null => {
  if (typeof country !== "string") {
    return null;
  }
  const trimmed = country.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};
const getVisibilityContext = ({
  showWaves,
  capacitorIsIos,
  country,
  isOwnProfile
}: {
  readonly showWaves: boolean;
  readonly capacitorIsIos: boolean;
  readonly country: string | null | undefined;
  readonly isOwnProfile: boolean;
}): UserPageVisibilityContext => {
  const normalizedCountry = normalizeCountry(country);
  return {
    showWaves,
    hideSubscriptions: capacitorIsIos && normalizedCountry !== "US",
    isOwnProfile
  };
};
const resolveTabFromPath = (pathname: string): UserPageTabKey => {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] ?? "";
  const match = getUserPageTabByRoute(routeSegment);
  return match?.id ?? DEFAULT_TAB;
};
export default function UserPageTabs() {
  const $ = _c(59);
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const params = useParams();
  let t0;
  if ($[0] !== params?.user) {
    t0 = params?.user?.toString() ?? "";
    $[0] = params?.user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleOrWallet = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams?.toString() ?? "";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchString = t1;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves,
    connectedProfile,
    fetchingProfile
  } = useAuth();
  let t2;
  if ($[4] !== connectedProfile || $[5] !== handleOrWallet) {
    t2 = isOwnProfileRoute({
      connectedProfile,
      handleOrWallet
    });
    $[4] = connectedProfile;
    $[5] = handleOrWallet;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const isOwnProfile = t2;
  let t3;
  if ($[7] !== capacitor.isIos || $[8] !== country || $[9] !== isOwnProfile || $[10] !== showWaves) {
    t3 = getVisibilityContext({
      showWaves,
      capacitorIsIos: capacitor.isIos,
      country,
      isOwnProfile
    });
    $[7] = capacitor.isIos;
    $[8] = country;
    $[9] = isOwnProfile;
    $[10] = showWaves;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const visibilityContext = t3;
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t4;
  if ($[12] !== pathname) {
    t4 = resolveTabFromPath(pathname);
    $[12] = pathname;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const resolvedTabFromPath = t4;
  const preserveProxyTabWhileOwnershipLoads = fetchingProfile && !connectedProfile && resolvedTabFromPath === USER_PAGE_TAB_IDS.PROXY;
  let t5;
  if ($[14] !== preserveProxyTabWhileOwnershipLoads || $[15] !== visibilityContext) {
    t5 = USER_PAGE_TABS.filter(tab => {
      if (preserveProxyTabWhileOwnershipLoads && tab.id === USER_PAGE_TAB_IDS.PROXY) {
        return true;
      }
      return tab.isVisible ? tab.isVisible(visibilityContext) : true;
    });
    $[14] = preserveProxyTabWhileOwnershipLoads;
    $[15] = visibilityContext;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const visibleTabs = t5;
  let t6;
  if ($[17] !== resolvedTabFromPath || $[18] !== visibleTabs) {
    let t7;
    if ($[20] !== resolvedTabFromPath) {
      t7 = tab_0 => tab_0.id === resolvedTabFromPath;
      $[20] = resolvedTabFromPath;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t6 = visibleTabs.some(t7);
    $[17] = resolvedTabFromPath;
    $[18] = visibleTabs;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const resolvedTabIsVisible = t6;
  let t7;
  bb0: {
    if (resolvedTabIsVisible) {
      t7 = resolvedTabFromPath;
      break bb0;
    }
    const firstVisibleTab = visibleTabs[0]?.id;
    t7 = firstVisibleTab ?? DEFAULT_TAB;
  }
  const activeTab = t7;
  let t8;
  if ($[22] !== handleOrWallet || $[23] !== pathname || $[24] !== resolvedTabIsVisible || $[25] !== router || $[26] !== searchString || $[27] !== visibleTabs[0] || $[28] !== visibleTabs.length) {
    t8 = () => {
      if (!visibleTabs.length || resolvedTabIsVisible || !handleOrWallet) {
        return;
      }
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab) {
        return;
      }
      const fallbackPath = fallbackTab.route ? `/${handleOrWallet}/${fallbackTab.route}` : `/${handleOrWallet}`;
      if (fallbackPath === pathname) {
        return;
      }
      const nextUrl = searchString ? `${fallbackPath}?${searchString}` : fallbackPath;
      router.replace(nextUrl);
    };
    $[22] = handleOrWallet;
    $[23] = pathname;
    $[24] = resolvedTabIsVisible;
    $[25] = router;
    $[26] = searchString;
    $[27] = visibleTabs[0];
    $[28] = visibleTabs.length;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== handleOrWallet || $[31] !== pathname || $[32] !== resolvedTabIsVisible || $[33] !== router || $[34] !== searchString || $[35] !== visibleTabs) {
    t9 = [handleOrWallet, pathname, resolvedTabIsVisible, router, searchString, visibleTabs];
    $[30] = handleOrWallet;
    $[31] = pathname;
    $[32] = resolvedTabIsVisible;
    $[33] = router;
    $[34] = searchString;
    $[35] = visibleTabs;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  useEffect(t8, t9);
  let t10;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const checkScroll = t10;
  let t11;
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(container_0);
        if (contentContainer) {
          resizeObserver.observe(contentContainer);
        }
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver?.disconnect();
      };
    };
    t12 = [checkScroll];
    $[38] = t11;
    $[39] = t12;
  } else {
    t11 = $[38];
    t12 = $[39];
  }
  useEffect(t11, t12);
  let t13;
  if ($[40] !== visibleTabs.length) {
    t13 = () => {
      if (!visibleTabs.length) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[40] = visibleTabs.length;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== visibleTabs) {
    t14 = [checkScroll, visibleTabs];
    $[42] = visibleTabs;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  useEffect(t13, t14);
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      container_1.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const scrollLeft_0 = t15;
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const scrollRight = t16;
  let t17;
  if ($[46] !== activeTab || $[47] !== visibleTabs) {
    t17 = visibleTabs.map(tabConfig => <UserPageTab key={tabConfig.id} tab={tabConfig} activeTabId={activeTab} parentRef={scrollContainerRef} />);
    $[46] = activeTab;
    $[47] = visibleTabs;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t17) {
    t18 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4" aria-label="Tabs">{t17}</div></div>;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== canScrollLeft) {
    t19 = canScrollLeft && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-group tw-absolute tw-left-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-start tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[51] = canScrollLeft;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== canScrollRight) {
    t20 = canScrollRight && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-group tw-absolute tw-right-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-end tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[53] = canScrollRight;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t18 || $[56] !== t19 || $[57] !== t20) {
    t21 = <div className="tw-relative tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700">{t18}{t19}{t20}</div>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  return t21;
}